/** Общие элементы редактора: карточки с действиями, меню добавления, сегментированные переключатели. */
import { h, icon } from '../dom.js';
import { popover } from '../ui/overlay.js';
import { store } from '../store.js';

/** Свернутые карточки (по id элемента), переживают перерисовку. */
export const collapsed = new Map<string, boolean>();

export interface CardActions {
  onUp?: () => void;
  onDown?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  extra?: HTMLElement[];
}

export interface ItemCardOpts {
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  accent?: string | null;
  actions?: CardActions;
  body: () => (HTMLElement | null)[];
  defaultCollapsed?: boolean;
  level?: 0 | 1 | 2;
  badge?: string;
}

export function itemCard(o: ItemCardOpts): HTMLElement {
  const isCollapsed = collapsed.get(o.id) ?? o.defaultCollapsed ?? false;
  const body = h('div', { class: 'card-body' });
  const fill = () => {
    if (!body.childElementCount) body.append(...(o.body().filter(Boolean) as HTMLElement[]));
  };
  if (!isCollapsed) fill();
  const a = o.actions ?? {};
  const act = (ic: string, title: string, fn?: () => void, cls = '') =>
    fn
      ? h(
          'button',
          {
            class: ['icon-btn', 'icon-btn-sm', cls],
            type: 'button',
            title,
            attrs: { 'aria-label': title },
            onclick: (e: MouseEvent) => {
              e.stopPropagation();
              fn();
            },
          },
          icon(ic, 15),
        )
      : null;

  const card: HTMLElement = h(
    'section',
    { class: ['item-card', `level-${o.level ?? 0}`, isCollapsed && 'is-collapsed'], dataset: { id: o.id } },
    h(
      'header',
      {
        class: 'card-head',
        onclick: () => {
          const next = !card.classList.contains('is-collapsed');
          card.classList.toggle('is-collapsed', next);
          collapsed.set(o.id, next);
          if (!next) fill();
        },
      },
      o.accent !== undefined ? h('span', { class: 'card-accent', style: { background: o.accent || 'rgba(255,255,255,.18)' } }) : null,
      o.icon ? h('span', { class: 'card-icon' }, icon(o.icon, 16)) : null,
      h('div', { class: 'card-titles' }, h('span', { class: 'card-title' }, o.title), o.subtitle ? h('span', { class: 'card-subtitle' }, o.subtitle) : null),
      o.badge ? h('span', { class: 'badge' }, o.badge) : null,
      h('div', { class: 'card-actions' }, a.extra ?? [], act('up', 'Выше', a.onUp), act('down', 'Ниже', a.onDown), act('copy', 'Дублировать', a.onDuplicate), act('trash', 'Удалить', a.onDelete, 'danger')),
      h('span', { class: 'card-chevron' }, icon('chevron', 16)),
    ),
    body,
  );
  return card;
}

/** Хелперы для массивов: возвращают действия, совместимые с историей. */
export function listActions<T>(arr: T[], index: number, clone: (x: T) => T, onDelete?: () => void): CardActions {
  return {
    onUp: index > 0 ? () => store.change(() => swap(arr, index, index - 1)) : undefined,
    onDown: index < arr.length - 1 ? () => store.change(() => swap(arr, index, index + 1)) : undefined,
    onDuplicate: () => store.change(() => arr.splice(index + 1, 0, clone(arr[index]))),
    onDelete: () => {
      store.change(() => arr.splice(index, 1));
      onDelete?.();
    },
  };
}

function swap<T>(arr: T[], a: number, b: number) {
  [arr[a], arr[b]] = [arr[b], arr[a]];
}

export interface AddItem {
  icon: string;
  label: string;
  desc?: string;
  disabled?: boolean;
  onClick: () => void;
}

export function addButton(label: string, items: AddItem[] | (() => void), opts: { disabled?: boolean; small?: boolean } = {}): HTMLButtonElement {
  const btn: HTMLButtonElement = h(
    'button',
    {
      class: ['btn', 'btn-add', opts.small && 'btn-sm'],
      type: 'button',
      disabled: !!opts.disabled,
      onclick: () => {
        if (typeof items === 'function') return items();
        const p = popover(
          btn,
          h(
            'div',
            { class: 'add-menu' },
            items.map((it) =>
              h(
                'button',
                {
                  class: 'add-menu-item',
                  type: 'button',
                  disabled: !!it.disabled,
                  onclick: () => {
                    p.close();
                    it.onClick();
                  },
                },
                h('span', { class: 'add-menu-icon' }, icon(it.icon, 18)),
                h('span', { class: 'add-menu-text' }, h('span', { class: 'add-menu-label' }, it.label), it.desc ? h('span', { class: 'add-menu-desc' }, it.desc) : null),
              ),
            ),
          ),
          { width: 290 },
        );
      },
    },
    icon('plus', 16),
    label,
  );
  return btn;
}

export function segmented<T extends string | number>(options: { value: T; label: string; icon?: string; cls?: string }[], value: T, onChange: (v: T) => void, label?: string): HTMLElement {
  const wrap = h('div', { class: 'segmented', attrs: { role: 'radiogroup', 'aria-label': label ?? '' } });
  for (const o of options) {
    const b = h(
      'button',
      {
        class: ['seg', o.cls, o.value === value && 'active'],
        type: 'button',
        attrs: { role: 'radio', 'aria-checked': o.value === value },
        onclick: () => {
          wrap.querySelectorAll('.seg').forEach((x) => {
            x.classList.remove('active');
            x.setAttribute('aria-checked', 'false');
          });
          b.classList.add('active');
          b.setAttribute('aria-checked', 'true');
          onChange(o.value);
        },
      },
      o.icon ? icon(o.icon, 14) : null,
      o.label,
    );
    wrap.append(b);
  }
  return wrap;
}

export function group(title: string | null, ...children: (HTMLElement | null)[]): HTMLElement {
  return h('div', { class: 'group' }, title ? h('div', { class: 'group-title' }, title) : null, h('div', { class: 'group-body' }, children));
}

export function row(...children: (HTMLElement | null)[]): HTMLElement {
  return h('div', { class: 'row-fields' }, children);
}

export function sectionPanel(opts: { id: string; title: string; icon: string; badge?: string; body: () => (HTMLElement | null)[]; defaultCollapsed?: boolean; extra?: HTMLElement[] }): HTMLElement {
  const isCollapsed = collapsed.get(opts.id) ?? opts.defaultCollapsed ?? false;
  const body = h('div', { class: 'panel-body' });
  const fill = () => {
    if (!body.childElementCount) body.append(...(opts.body().filter(Boolean) as HTMLElement[]));
  };
  if (!isCollapsed) fill();
  const panel: HTMLElement = h(
    'section',
    { class: ['panel', 'glass', isCollapsed && 'is-collapsed'], dataset: { id: opts.id } },
    h(
      'header',
      {
        class: 'panel-head',
        onclick: () => {
          const next = !panel.classList.contains('is-collapsed');
          panel.classList.toggle('is-collapsed', next);
          collapsed.set(opts.id, next);
          if (!next) fill();
        },
      },
      h('span', { class: 'panel-icon' }, icon(opts.icon, 18)),
      h('h3', { class: 'panel-title' }, opts.title),
      opts.badge ? h('span', { class: 'badge' }, opts.badge) : null,
      h('div', { class: 'panel-extra', onclick: (e: MouseEvent) => e.stopPropagation() }, opts.extra ?? []),
      h('span', { class: 'card-chevron' }, icon('chevron', 18)),
    ),
    body,
  );
  return panel;
}

export function emptyHint(text: string): HTMLElement {
  return h('div', { class: 'empty-hint' }, icon('sparkles', 16), text);
}
